import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, rupiah, LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Copy, Check, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const qrSrc = (p) =>
  p.qr_image ? p.qr_image
    : p.qr_string ? `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(p.qr_string)}`
    : "";

export default function PaymentInstructions() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | pending | success | failed
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState("");
  const timer = useRef(null);
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  const check = useCallback(async () => {
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setInfo(data);
      if (data.payment_status === "paid") { setState("success"); return true; }
      if (data.status === "failed" || data.status === "expired") { setState("failed"); return true; }
      setState("pending");
    } catch (e) { /* keep polling */ }
    return false;
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let stop = false;
    const loop = async () => {
      const done = await check();
      if (!done && !stop) timer.current = setTimeout(loop, 5000);
    };
    loop();
    return () => { stop = true; if (timer.current) clearTimeout(timer.current); };
  }, [sessionId, check]);

  // Countdown to expiry
  useEffect(() => {
    const exp = info?.payment?.expired;
    if (!exp || state !== "pending") return;
    const end = new Date(exp.replace(" ", "T")).getTime();
    const t = setInterval(() => {
      const diff = end - Date.now();
      if (diff <= 0) { setRemaining("Kadaluarsa"); clearInterval(t); return; }
      const h = Math.floor(diff / 3600000), m = Math.floor((diff % 3600000) / 60000), s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${h > 0 ? h + "j " : ""}${m}m ${s}d`);
    }, 1000);
    return () => clearInterval(t);
  }, [info, state]);

  const copyVa = () => {
    navigator.clipboard.writeText(info.payment.payment_no);
    setCopied(true);
    toast.success("Nomor Virtual Account disalin");
    setTimeout(() => setCopied(false), 2000);
  };

  if (state === "loading")
    return <Center><Loader2 size={48} className="text-emerald-500 animate-spin" /><p className="mt-4 text-stone-500">Menyiapkan pembayaran…</p></Center>;

  if (state === "success")
    return (
      <Center>
        <CheckCircle2 size={64} className="text-emerald-500" />
        <h1 className="font-heading text-2xl font-bold text-stone-900 mt-4" data-testid="payment-success">Pembayaran Berhasil!</h1>
        <p className="text-stone-500 mt-2 text-center">{info?.description} sebesar {rupiah(info?.amount)} telah diterima. Saldo/tagihan telah diperbarui.</p>
        <Button onClick={() => navigate("/parent")} data-testid="back-to-dashboard" className="mt-8 w-full py-6 rounded-xl bg-emerald-600 hover:bg-emerald-700">Kembali ke Beranda</Button>
      </Center>
    );

  if (state === "failed")
    return (
      <Center>
        <XCircle size={64} className="text-rose-500" />
        <h1 className="font-heading text-2xl font-bold text-stone-900 mt-4" data-testid="payment-failed">Pembayaran Gagal / Kadaluarsa</h1>
        <p className="text-stone-500 mt-2 text-center">Pembayaran tidak selesai atau melewati batas waktu. Tidak ada saldo terpotong. Silakan coba lagi.</p>
        <Button onClick={() => navigate("/parent/bayar")} className="mt-8 w-full py-6 rounded-xl bg-emerald-600 hover:bg-emerald-700">Coba Lagi</Button>
        <Button variant="ghost" onClick={() => navigate("/parent")} className="mt-2 w-full">Kembali ke Beranda</Button>
      </Center>
    );

  // pending
  const p = info?.payment || {};
  const qr = qrSrc(p);
  return (
    <Center>
      <img src={LOGO_URL} alt="" className="w-12 h-12 object-contain" />
      <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5" data-testid="payment-waiting-badge">
        <Loader2 size={14} className="animate-spin" /> Menunggu Pembayaran
      </span>
      <p className="mt-3 text-stone-500 text-center text-sm">{info?.description}</p>
      <p className="font-heading text-3xl font-bold text-stone-900 mt-1">{rupiah(info?.amount)}</p>
      {remaining && <p className="mt-1 text-xs text-stone-400 flex items-center gap-1"><Clock size={12} /> Bayar sebelum {remaining}</p>}

      <div className="w-full mt-6 rounded-2xl border border-stone-200 bg-white p-5">
        {qr ? (
          <div className="flex flex-col items-center" data-testid="payment-qr">
            <p className="text-sm font-semibold text-stone-700 mb-3">Scan QRIS di bawah ini</p>
            <img src={qr} alt="QRIS" className="w-56 h-56 rounded-lg border border-stone-100" />
            <p className="text-xs text-stone-400 mt-3 text-center">Buka aplikasi bank / e-wallet Anda, pilih QRIS, lalu scan.</p>
          </div>
        ) : p.payment_no ? (
          <div data-testid="payment-va">
            <p className="text-xs uppercase tracking-wide text-stone-400 font-semibold">Virtual Account {p.channel}</p>
            <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-stone-50 border border-stone-200 px-4 py-3">
              <span className="font-mono text-lg font-bold text-stone-900 tracking-wide" data-testid="va-number">{p.payment_no}</span>
              <Button size="sm" variant="outline" onClick={copyVa} data-testid="copy-va" className="rounded-lg">
                {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
              </Button>
            </div>
            {p.payment_name && <p className="text-xs text-stone-400 mt-2">a.n. {p.payment_name}</p>}
            <div className="mt-4 text-sm text-stone-600 space-y-1.5">
              <p className="font-semibold text-stone-700">Cara bayar:</p>
              <ol className="list-decimal list-inside space-y-1 text-stone-500">
                <li>Buka aplikasi / ATM {p.channel}.</li>
                <li>Pilih menu <b>Transfer → Virtual Account</b>.</li>
                <li>Masukkan nomor VA di atas.</li>
                <li>Pastikan nominal <b>{rupiah(info?.amount)}</b>, lalu selesaikan pembayaran.</li>
              </ol>
            </div>
          </div>
        ) : (
          <p className="text-center text-sm text-stone-500">Silakan selesaikan pembayaran sesuai instruksi yang diberikan.</p>
        )}
      </div>

      <Button onClick={check} variant="outline" data-testid="check-status-btn" className="mt-4 w-full py-5 rounded-xl">
        <RefreshCw size={16} className="mr-2" /> Saya Sudah Bayar / Cek Status
      </Button>
      <p className="text-[11px] text-stone-400 mt-2 text-center">Status pembayaran diperbarui otomatis. Halaman ini akan berubah sendiri setelah pembayaran diterima.</p>
      <Button variant="ghost" onClick={() => navigate("/parent")} className="mt-1 w-full text-stone-500">Bayar Nanti</Button>
    </Center>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
    <div className="bg-white border border-stone-200 rounded-2xl shadow-lg p-8 max-w-md w-full flex flex-col items-center animate-fade-up">
      {children}
    </div>
  </div>
);
